/**
 * Client-side Delta Application
 * 
 * Applies pre-computed delta maps to transform base templates into personalized versions.
 * This is a client-side implementation that works with frontend types.
 * 
 * @see convex/ai/deltaEngine.ts - Server-side reference implementation
 * @see docs/project/specs/dynamic-template-system-v2-spec.md
 */

import type { ProjectTemplate, TemplateStep } from '../types'

// Delta types (matching server-side definitions)
export interface StepPatch {
  title?: string
  description?: string
  slug?: string
  estimatedMinutes?: number
  tips?: Array<string>
  warnings?: Array<string>
  tools?: Array<{ name?: string; customName?: string; required: boolean }>
  materials?: Array<{ name?: string; customName?: string; quantity?: number; unit?: string; estimatedCost?: number }>
}

export interface ModifyStepDelta {
  index: number
  patch: StepPatch
}

/** Inserted step as stored in Convex (tools/materials are string[]; slug optional) */
export interface AddStepDelta {
  afterStepIndex: number
  step: {
    title: string
    description: string
    slug?: string
    sequenceNumber?: number
    estimatedMinutes?: number
    tools?: Array<string> | Array<{ name?: string; customName?: string; required: boolean }>
    materials?: Array<string> | Array<{ name?: string; customName?: string; quantity?: number; unit?: string; estimatedCost?: number }>
    tips?: Array<string>
    warnings?: Array<string>
    sectionSlug?: string
  }
}

export interface AddMaterialDelta {
  name: string
  quantity: number | string
  unit?: string
  estimatedCost?: number
}

export interface Delta {
  addSteps?: Array<AddStepDelta>
  removeStepIndices?: Array<number>
  modifySteps?: Array<ModifyStepDelta>
  addTools?: Array<string>
  addMaterials?: Array<AddMaterialDelta>
  adjustHours?: number
  adjustCost?: { min: number; max: number }
}

export interface AnswerDelta {
  questionKey: string
  answerValue: string
  delta: Delta
}

export interface PersonalizedTemplate {
  title: string
  estimatedHours: number
  estimatedCostMin: number
  estimatedCostMax: number
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  steps: Array<TemplateStep>
  modifiedStepIndices: Set<number>
}

/**
 * Apply delta maps to a template based on user answers.
 * 
 * @param template - Base template
 * @param answers - Map of question key to answer value
 * @param deltas - Array of delta definitions
 * @returns Personalized template with modifications tracked
 */
export function applyDeltas(
  template: ProjectTemplate,
  answers: Record<string, string>,
  deltas: Array<AnswerDelta>
): PersonalizedTemplate {
  // Deep clone steps to avoid mutating original
  let steps: Array<TemplateStep> = JSON.parse(JSON.stringify(template.steps))
  const originalStepCount = steps.length
  
  let estimatedHours = template.estimatedHours ?? 0
  let estimatedCostMin = template.estimatedCostMin ?? 0
  let estimatedCostMax = template.estimatedCostMax ?? 0
  
  const indicesToRemove = new Set<number>()
  const modifiedStepIndices = new Set<number>()
  const allAdditions: Array<AddStepDelta> = []
  
  // Process each answer's delta
  for (const [key, value] of Object.entries(answers)) {
    if (!value || value.trim() === '') continue
    
    const delta = deltas.find(d => d.questionKey === key && d.answerValue === value)
    if (!delta?.delta) continue
    
    const d = delta.delta
    
    // Collect step removals
    if (d.removeStepIndices) {
      for (const idx of d.removeStepIndices) {
        if (idx >= 0 && idx < originalStepCount) {
          indicesToRemove.add(idx)
        }
      }
    }
    
    // Collect step additions
    if (d.addSteps) {
      allAdditions.push(...d.addSteps)
    }
    
    // Apply step modifications (patch may include title/description from server v.any())
    if (d.modifySteps) {
      for (const mod of d.modifySteps) {
        if (mod.index >= 0 && mod.index < steps.length) {
          modifiedStepIndices.add(mod.index)
          const step = steps[mod.index]
          const patch = mod.patch as StepPatch & Record<string, unknown>

          if (typeof patch.title === 'string' && patch.title.length > 0) {
            step.title = patch.title
          }
          if (typeof patch.description === 'string' && patch.description.length > 0) {
            step.description = patch.description
          }
          if (typeof patch.slug === 'string' && patch.slug.length > 0) {
            step.slug = patch.slug
          }

          if (patch.estimatedMinutes !== undefined) {
            step.estimatedMinutes = patch.estimatedMinutes
          }

          if (patch.tips) {
            step.tips = mergeArrays(step.tips, patch.tips)
          }

          if (patch.warnings) {
            step.warnings = mergeArrays(step.warnings, patch.warnings)
          }

          if (patch.tools) {
            step.tools = mergeToolArrays(step.tools, patch.tools)
          }

          if (patch.materials) {
            step.materials = mergeMaterialArrays(step.materials, patch.materials)
          }
        }
      }
    }

    // Merge delta-level tool/material additions (pilot deltas use these alongside modifySteps)
    const extrasTarget = targetStepIndexForDeltaExtras(d, steps.length)
    if (extrasTarget >= 0 && d.addTools?.length) {
      const toolObjs = d.addTools.map((name) => ({ name, required: true as const }))
      steps[extrasTarget].tools = mergeToolArrays(steps[extrasTarget].tools, toolObjs)
      modifiedStepIndices.add(extrasTarget)
    }
    if (extrasTarget >= 0 && d.addMaterials?.length) {
      const mats = d.addMaterials.map((m) => ({
        name: m.name,
        quantity: typeof m.quantity === 'number' ? m.quantity : undefined,
        unit: m.unit,
        estimatedCost: m.estimatedCost,
      }))
      steps[extrasTarget].materials = mergeMaterialArrays(steps[extrasTarget].materials, mats)
      modifiedStepIndices.add(extrasTarget)
    }

    // Apply time adjustments
    if (d.adjustHours !== undefined) {
      estimatedHours = Math.max(0, estimatedHours + d.adjustHours)
    }
    
    // Apply cost adjustments
    if (d.adjustCost) {
      estimatedCostMin = Math.max(0, estimatedCostMin + d.adjustCost.min)
      estimatedCostMax = Math.max(0, estimatedCostMax + d.adjustCost.max)
    }
  }
  
  // Remap modified indices after removals, then drop removed steps
  let workingModified = modifiedStepIndices
  if (indicesToRemove.size > 0) {
    workingModified = remapIndicesAfterRemoval(workingModified, indicesToRemove)
    steps = steps.filter((_, idx) => !indicesToRemove.has(idx))
  }

  // Add steps in reverse order to maintain positions
  const sortedAdditions = [...allAdditions].sort((a, b) => b.afterStepIndex - a.afterStepIndex)
  const sortedRemovals = [...indicesToRemove].sort((a, b) => a - b)

  for (const addition of sortedAdditions) {
    let adjustedIndex = addition.afterStepIndex
    for (const removedIdx of sortedRemovals) {
      if (removedIdx <= adjustedIndex) {
        adjustedIndex--
      }
    }

    const insertAt = Math.min(Math.max(0, adjustedIndex + 1), steps.length)
    workingModified = shiftIndicesForInsertion(workingModified, insertAt)

    const newStep = templateStepFromDeltaAddition(addition, insertAt)

    steps.splice(insertAt, 0, newStep)
  }

  const finalModifiedIndices = workingModified
  
  // Re-sequence step numbers
  steps = steps.map((step, idx) => ({
    ...step,
    sequenceNumber: idx + 1,
  }))
  
  return {
    title: template.title,
    estimatedHours,
    estimatedCostMin,
    estimatedCostMax,
    difficulty: template.difficulty,
    steps,
    modifiedStepIndices: finalModifiedIndices,
  }
}

function targetStepIndexForDeltaExtras(d: Delta, stepsLen: number): number {
  if (stepsLen <= 0) return -1
  if (d.modifySteps?.length) {
    return Math.min(...d.modifySteps.map((m) => m.index))
  }
  return 0
}

function remapIndicesAfterRemoval(indices: Set<number>, removed: Set<number>): Set<number> {
  const out = new Set<number>()
  for (const idx of indices) {
    if (removed.has(idx)) continue
    let n = idx
    for (const r of removed) {
      if (r < idx) n--
    }
    out.add(n)
  }
  return out
}

function shiftIndicesForInsertion(indices: Set<number>, insertAt: number): Set<number> {
  const out = new Set<number>()
  for (const idx of indices) {
    out.add(idx >= insertAt ? idx + 1 : idx)
  }
  out.add(insertAt)
  return out
}

function normalizeToolsFromDeltaStep(
  tools: AddStepDelta['step']['tools']
): TemplateStep['tools'] | undefined {
  if (!tools || tools.length === 0) return tools as undefined
  if (typeof tools[0] === 'string') {
    return (tools as Array<string>).map((name) => ({ name, required: true }))
  }
  return tools as TemplateStep['tools']
}

function normalizeMaterialsFromDeltaStep(
  materials: AddStepDelta['step']['materials']
): TemplateStep['materials'] | undefined {
  if (!materials || materials.length === 0) return materials as undefined
  if (typeof materials[0] === 'string') {
    return (materials as Array<string>).map((name) => ({ name, customName: name, quantity: 1 }))
  }
  return materials as TemplateStep['materials']
}

function slugForInsertedStep(title: string, insertAt: number): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
  return `${base || 'step'}-delta-${insertAt}`
}

function templateStepFromDeltaAddition(addition: AddStepDelta, insertAt: number): TemplateStep {
  const { step } = addition
  const slug = step.slug && step.slug.length > 0 ? step.slug : slugForInsertedStep(step.title, insertAt)
  return {
    sequenceNumber: step.sequenceNumber ?? insertAt + 1,
    slug,
    title: step.title,
    description: step.description,
    estimatedMinutes: step.estimatedMinutes,
    tools: normalizeToolsFromDeltaStep(step.tools),
    materials: normalizeMaterialsFromDeltaStep(step.materials),
    tips: step.tips,
    warnings: step.warnings,
    sectionSlug: step.sectionSlug,
  }
}

function mergeArrays<T>(existing: Array<T> | undefined, additions: Array<T> | undefined): Array<T> {
  if (!additions || additions.length === 0) return existing ?? []
  if (!existing || existing.length === 0) return additions
  return [...new Set([...existing, ...additions])]
}

function mergeToolArrays(
  existing: TemplateStep['tools'],
  additions: TemplateStep['tools']
): TemplateStep['tools'] {
  if (!additions || additions.length === 0) return existing
  if (!existing || existing.length === 0) return additions
  
  const existingNames = new Set(existing.map(t => t.name ?? t.customName))
  const newTools = additions.filter(t => {
    const name = t.name ?? t.customName
    return name && !existingNames.has(name)
  })
  
  return [...existing, ...newTools]
}

function mergeMaterialArrays(
  existing: TemplateStep['materials'],
  additions: TemplateStep['materials']
): TemplateStep['materials'] {
  if (!additions || additions.length === 0) return existing
  if (!existing || existing.length === 0) return additions
  
  const existingNames = new Set(existing.map(m => m.name ?? m.customName))
  const newMaterials = additions.filter(m => {
    const name = m.name ?? m.customName
    return name && !existingNames.has(name)
  })
  
  return [...existing, ...newMaterials]
}

/**
 * Check if all user answers have delta mappings.
 */
export function checkDeltaCoverage(
  answers: Record<string, string>,
  deltas: Array<AnswerDelta>
): { allMapped: boolean; unmappedKeys: Array<string> } {
  const unmappedKeys: Array<string> = []
  
  for (const [key, value] of Object.entries(answers)) {
    if (!value || value.trim() === '') continue
    
    const hasDelta = deltas.some(d => d.questionKey === key && d.answerValue === value)
    if (!hasDelta) {
      unmappedKeys.push(key)
    }
  }
  
  return {
    allMapped: unmappedKeys.length === 0,
    unmappedKeys,
  }
}
